import { z } from 'zod';
export const canvasAssetSourceSchema = z.enum(['jobOutput', 'upload', 'external']);
export const canvasAssetStatusSchema = z.enum(['active', 'hidden', 'archived']);
export const canvasVariantStatusSchema = z.enum(['pending', 'succeeded', 'failed']);
export const canvasFlowVisibilitySchema = z.enum(['private', 'shared', 'public']);
export const canvasModelInfoSchema = z
    .object({
    provider: z.string(),
    model: z.string(),
    label: z.string().optional(),
    version: z.string().optional(),
    mode: z.enum(['image', 'video', 'audio']).optional()
})
    .strict();
export const canvasSceneBundleMetadataSchema = z
    .object({
    kind: z.literal('scene_bundle'),
    storyboardId: z.string().optional(),
    storyboardName: z.string().optional(),
    sceneId: z.string().optional(),
    sceneOrder: z.number().int().nonnegative().optional(),
    sceneSummary: z.string().optional(),
    durationSec: z.number().int().positive().optional(),
    aspectRatio: z.string().optional()
})
    .partial()
    .strict();
export const canvasSceneAssetMetadataSchema = z
    .object({
    kind: z.literal('scene_asset'),
    sceneBundleId: z.string().optional(),
    storyboardId: z.string().optional(),
    sceneId: z.string().optional(),
    variationId: z.string().optional(),
    role: z.enum(['image', 'video']).optional()
})
    .partial()
    .strict();
export const canvasVariantSchema = z
    .object({
    id: z.string(),
    assetId: z.string(),
    jobId: z.string().optional(),
    storagePath: z.string().optional(),
    assetUrl: z.string().url(),
    thumbnailUrl: z.string().url().optional(),
    status: canvasVariantStatusSchema.default('pending'),
    prompt: z.string().optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
    modelInfo: canvasModelInfoSchema.optional(),
    cost: z.number().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime()
})
    .strict();
export const canvasAssetSchema = z
    .object({
    id: z.string(),
    ownerId: z.string(),
    jobId: z.string().optional(),
    flowId: z.string().optional(),
    parentAssetId: z.string().optional(),
    sourceType: canvasAssetSourceSchema,
    status: canvasAssetStatusSchema,
    order: z.number().int().nonnegative().optional(),
    assetUrl: z.string().url().optional(),
    thumbnailUrl: z.string().url().optional(),
    storagePath: z.string().optional(),
    storageBucket: z.string().optional(),
    hiddenAt: z.string().datetime().optional(),
    variantIds: z.array(z.string()).default([]),
    modelInfo: canvasModelInfoSchema.optional(),
    prompt: z.string().optional(),
    aspectRatio: z.string().optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime()
})
    .strict()
    .superRefine((value, ctx) => {
    const hasAssetUrl = typeof value.assetUrl === 'string' && value.assetUrl.length > 0;
    const hasStoragePath = typeof value.storagePath === 'string' && value.storagePath.length > 0;
    if (!hasAssetUrl && !hasStoragePath) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Canvas asset requires either assetUrl or storagePath.',
            path: ['assetUrl']
        });
    }
});
export const canvasFlowSummarySchema = z
    .object({
    totalCredits: z.number().optional(),
    totalDurationSeconds: z.number().optional(),
    variantCount: z.number().int().nonnegative().optional(),
    assetCount: z.number().int().nonnegative().optional()
})
    .strict();
export const canvasFlowSchema = z
    .object({
    id: z.string(),
    ownerId: z.string(),
    name: z.string().min(1),
    description: z.string().optional(),
    assetIds: z.array(z.string()),
    startAssetId: z.string().optional(),
    endAssetId: z.string().optional(),
    transitionModel: canvasModelInfoSchema.optional(),
    visibility: canvasFlowVisibilitySchema.default('private'),
    summary: canvasFlowSummarySchema.optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime()
})
    .strict();
export const canvasBridgeRequestSchema = z
    .object({
    flowId: z.string(),
    startAssetId: z.string(),
    endAssetId: z.string(),
    model: z.string(),
    settings: z.record(z.string(), z.unknown()).optional()
})
    .strict();
export const canvasGenerateVariantRequestSchema = z
    .object({
    assetId: z.string(),
    model: z.string(),
    prompt: z.string().min(1),
    settings: z.record(z.string(), z.unknown()).optional(),
    inputAssets: z
        .array(z.object({
        field: z.string(),
        storagePath: z.string(),
        storageBucket: z.string().optional(),
        url: z.string().url().optional(),
        index: z.number().int().nonnegative().optional()
    }))
        .optional()
})
    .strict();
export const canvasAssetListFiltersSchema = z
    .object({
    flowId: z.string().optional(),
    unlinkedOnly: z.boolean().optional(),
    status: canvasAssetStatusSchema.optional(),
    search: z.string().optional(),
    includeHidden: z.boolean().optional()
})
    .strict();
//# sourceMappingURL=canvas.js.map